import React, { useState, useEffect } from 'react';
import { X, Edit, Calendar, FileText, CheckCircle, Trash2, CheckSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import { fetchSolutions } from '../utils/supabaseHelpers';
import { formatSimpleDate } from '../utils/dateFormatter';

const ProblemDetailModal = ({ problem, onClose, onEdit, onUpdate, onDelete }) => {
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(problem.status || 'Not Decided');
  // If the task has a saved done_date, use it. Otherwise default to today.
  const [doneDate, setDoneDate] = useState(
    problem.done_date ? new Date(problem.done_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [solutions, setSolutions] = useState([]);
  const [loadingSolutions, setLoadingSolutions] = useState(false);
  const [expandedSolutions, setExpandedSolutions] = useState({});

  useEffect(() => {
    // Use solutions from problem object (loaded via LEFT JOIN) or fallback to empty array
    const problemSolutions = problem.solutions || [];
    setSolutions(problemSolutions);
    setLoadingSolutions(false);
    
    // Initialize selectedStatus to current problem status
    setSelectedStatus(problem.status || 'Not Decided');
    
    // Initialize doneDate from problem or set to today
    if (problem.done_date) {
      setDoneDate(new Date(problem.done_date).toISOString().split('T')[0]);
    } else {
      setDoneDate(new Date().toISOString().split('T')[0]);
    }

    // Reset expanded solutions state when problem changes
    setExpandedSolutions({});
  }, [problem]);

  const handleStatusUpdate = () => {
    const updates = { ...problem, status: selectedStatus };
    if (selectedStatus === 'Done') {
      updates.done_date = doneDate;
    } else {
      updates.done_date = null; // Clear date if reopened
    }
    onUpdate(updates);
    setShowStatusUpdate(false);
  };

  const toggleSolutionExpanded = (subtaskId) => {
    setExpandedSolutions(prev => ({
      ...prev,
      [subtaskId]: !prev[subtaskId]
    }));
  };

  const getDomainStyle = (domain) => {
    const d = domain?.toLowerCase() || '';
    switch(d) {
      case 'electronics': return 'text-blue-400';
      case 'mechanical': return 'text-purple-400';
      case 'software': return 'text-green-400';
      case 'operations': return 'text-orange-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusStyle = (status) => {
    switch(status) {
      case 'Not Decided': return 'text-gray-400';
      case 'In Progress': return 'text-blue-400';
      case 'Hold': return 'text-yellow-500';
      case 'Done': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getPriorityStyle = (priority) => {
    const p = priority?.toLowerCase() || '';
    switch(p) {
      case 'critical': return 'text-red-400';
      case 'high': return 'text-orange-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="problem-detail-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-2xl font-bold flex-1 pr-4" data-testid="modal-title">{problem.title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="modal-close-btn"
            >
              <X size={24} />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDomainStyle(problem.domain)}`}>
              {problem.domain?.charAt(0).toUpperCase() + problem.domain?.slice(1)}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusStyle(problem.status)}`}>
              {formatStatus(problem.status)}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityStyle(problem.priority)}`}>
              {problem.priority?.charAt(0).toUpperCase() + problem.priority?.slice(1)}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 scrollbar-thin" style={{ maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}>


          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <FileText size={20} className="text-blue-400" />
              Description
            </h3>
            <p className="text-gray-300" data-testid="modal-description">{problem.description}</p>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <Calendar size={16} />
                <span className="text-sm">Date Reported</span>
              </div>
              <p className="font-medium" data-testid="modal-date">
                {formatSimpleDate(problem.date_reported || problem.dateReported || problem.created_at)}
              </p>
            </div>
            
            {problem.updated_at && problem.updated_at !== problem.created_at && (
              <div className="glass-card p-4">
                <div className="flex items-center gap-2 text-gray-400 mb-2">
                  <Calendar size={16} />
                  <span className="text-sm">Last Updated</span>
                </div>
                <p className="font-medium" data-testid="modal-updated-date">
                  {formatSimpleDate(problem.updated_at)}
                </p>
              </div>
            )}
            
            {(problem.due_date || problem.dueDate) && (
              <div className="glass-card p-4">
                <div className="flex items-center gap-2 text-gray-400 mb-2">
                  <Calendar size={16} />
                  <span className="text-sm">Due Date</span>
                </div>
                <p className="font-medium" data-testid="modal-due-date">
                  {formatSimpleDate(problem.due_date || problem.dueDate)}
                </p>
              </div>
            )}
            
            {problem.done_date && (
              <div className="glass-card p-4">
                <div className="flex items-center gap-2 text-gray-400 mb-2">
                  <CheckCircle size={16} />
                  <span className="text-sm">Completed Date</span>
                </div>
                <p className="font-medium text-green-400" data-testid="modal-done-date">
                  {formatSimpleDate(problem.done_date)}
                </p>
              </div>
            )}
          </div>

          {/* Solutions Section */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4 text-white">Solutions</h2>
            {problem?.solutions && problem.solutions.length > 0 ? (
              <div className="space-y-3">
                {problem.solutions.map((solution, index) => (
                  <div 
                    key={solution.id || index}
                    className={`p-4 rounded ${
                      solution.is_chosen 
                        ? 'border-2 border-green-500' 
                        : 'bg-gray-800 border border-gray-700'
                    }`}
                  >
                    <p className="text-gray-200">{solution.description}</p>
                    {solution.is_chosen && (
                      <span className="text-sm text-green-400 font-semibold mt-2 inline-block">
                        Chosen Solution
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400">No solutions added yet.</p>
            )}
          </div>

          {/* Subtasks Section */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4 text-white flex items-center gap-2">
              <CheckSquare size={20} className="text-blue-400" />
              Subtasks
            </h2>
            {problem?.subtasks && problem.subtasks.length > 0 ? (
              <div className="space-y-3">
                {[...problem.subtasks].sort((a, b) => {
                  // Incomplete tasks first
                  if (!a.is_completed && b.is_completed) return -1;
                  if (a.is_completed && !b.is_completed) return 1;
                  
                  // Both completed - sort by completed_date (most recent first)
                  if (a.is_completed && b.is_completed) {
                    const dateA = a.completed_date ? new Date(a.completed_date).getTime() : 0;
                    const dateB = b.completed_date ? new Date(b.completed_date).getTime() : 0;
                    return dateB - dateA; // Most recent first
                  }
                  
                  // Both incomplete - maintain original order
                  return 0;
                }).map((subtask, index) => (
                  <div 
                    key={subtask.id || index}
                    className={`p-4 rounded-lg border transition-all ${
                      subtask.is_completed 
                        ? 'bg-white/5 border-gray-600' 
                        : 'bg-gray-800 border-gray-700'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={subtask.is_completed}
                        readOnly
                        className="mt-1 w-4 h-4 cursor-default"
                      />
                      <div className="flex-1">
                        <p className={`font-medium ${subtask.is_completed ? 'text-gray-400' : 'text-gray-200'}`}>
                          {subtask.title}
                        </p>
                        {subtask.due_date && (
                          <p className="text-sm text-gray-500 mt-1">
                            Due: {formatSimpleDate(subtask.due_date)}
                          </p>
                        )}
                        {subtask.is_completed && subtask.solution && (
                          <div className="mt-3">
                            <button
                              onClick={() => toggleSolutionExpanded(subtask.id || index)}
                              className="flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                            >
                              {expandedSolutions[subtask.id || index] ? (
                                <>
                                  <ChevronUp size={16} />
                                  Hide Solution
                                </>
                              ) : (
                                <>
                                  <ChevronDown size={16} />
                                  View Solution
                                </>
                              )}
                            </button>
                            {expandedSolutions[subtask.id || index] && (
                              <div className="mt-2 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                                <p className="text-xs font-medium text-gray-400 mb-1">Solution:</p>
                                <p className="text-sm text-gray-300 leading-relaxed">
                                  {subtask.solution}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {subtask.is_completed && (
                        <div className="text-right">
                          <div className="text-xs text-green-400 font-medium">Completed</div>
                          {subtask.completed_date && (
                            <div className="text-xs text-green-400 mt-0.5">
                              {format(new Date(subtask.completed_date), 'dd-MM-yyyy')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400">No subtasks added yet.</p>
            )}
          </div>

          {/* Notes */}
          {problem.notes && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Notes</h3>
              <div className="glass-card p-4">
                <p className="text-gray-300 whitespace-pre-wrap" data-testid="modal-notes">{problem.notes}</p>
              </div>
            </div>
          )}

          {/* Documents */}
          {problem.linkedDocuments && problem.linkedDocuments.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Linked Documents</h3>
              <div className="glass-card p-4">
                <p className="text-gray-400 text-sm">{problem.linkedDocuments.length} document(s) linked</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-white/10 flex gap-3">
          <button
            onClick={onEdit}
            className="btn-primary flex items-center gap-2"
            data-testid="modal-edit-btn"
          >
            <Edit size={18} />
            Edit
          </button>
          <button
            onClick={() => {
              setShowStatusUpdate(!showStatusUpdate);
              // Reset selectedStatus to current problem status when toggling
              if (!showStatusUpdate) {
                setSelectedStatus(problem.status || 'Not Decided');
              }
            }}
            className="btn-secondary"
            data-testid="modal-update-status-btn"
          >
            Update Status
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              className="btn-secondary text-red-400 hover:bg-red-500/10 flex items-center gap-2 ml-auto"
              data-testid="modal-delete-btn"
            >
              <Trash2 size={18} />
              Delete
            </button>
          )}
          {showStatusUpdate && (
            <div className="flex-1 flex flex-col gap-2">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="glass-input flex-1"
                data-testid="status-select"
              >
                <option value="Not Decided">Not Decided</option>
                <option value="In Progress">In Progress</option>
                <option value="Hold">Hold</option>
                <option value="Done">Done</option>
              </select>
              {selectedStatus === 'Done' && (
                <div className="mt-3 p-3 bg-gray-800/50 border border-gray-700 rounded-md">
                  <label className="text-xs text-gray-400 block mb-1">Completion Date</label>
                  <input 
                    type="date" 
                    value={doneDate}
                    onChange={(e) => setDoneDate(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-700 text-white text-sm rounded px-2 py-1 focus:border-blue-500 outline-none"
                    data-testid="done-date-input"
                  />
                </div>
              )}
              <button
                onClick={handleStatusUpdate}
                className="btn-primary"
                data-testid="status-update-confirm-btn"
              >
                Update
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProblemDetailModal;
